import { useAuthStore } from '@/stores/auth';

export const useDevOnly = (authorizedEmails = []) => {
    // Default email addresses that have dev access in production
    const defaultAuthorizedDevEmails = [];

    // Combine default emails with any additional emails passed as parameters
    const allAuthorizedEmails = [...defaultAuthorizedDevEmails, ...authorizedEmails];

    const isAuthorizedDevUser = (additionalEmails = []) => {
        const authStore = useAuthStore();
        const userEmail = authStore.user?.apiUser?.email;
        const emailsToCheck = [...allAuthorizedEmails, ...additionalEmails];
        return !!(userEmail && emailsToCheck.includes(userEmail));
    };

    const isDev = (options = {}) => {
        // Extract emails and databases from options object
        const emails = options.allowedEmails || [];
        const databases = options.allowedDatabases || [];

        return import.meta.env.DEV || isTenant('freshqa', ...databases) || isAuthorizedDevUser(emails);
    };
    const devOnly = isDev;

    const isTenant = (...allowedDatabases) => {
        const authStore = useAuthStore();
        return allowedDatabases.some((db) => authStore.account?.fhirApiUri?.includes(db));
    };

    /** Branch QA tenant for RCM rails only — not a general preview/dev gate. */
    const isProaaTenant = () => isTenant('proaa');

    /** Preview/dev or proaa branch tenant — use only for Availity/Fortis/ledger orchestration UI. */
    const isRcmRailPreview = () => isDev() || isProaaTenant();

    /**
     * Messaging pilot gate — mirrors the server's MessagingExposure (non-prod, or proaa/freshqa on
     * prod). Gates only the control that turns account messaging ON; every other messaging surface
     * keys off `authStore.messagingEnabled`, which AccountStateProcessor forces off for an unexposed
     * tenant, so re-checking this elsewhere would be a second copy of one rule.
     */
    const isMessagingPreview = () => isDev() || isProaaTenant();

    const isProfile = () => isTenant('bnprofile');

    const isBnSupport = () => {
        const authStore = useAuthStore();
        return authStore.isBnSupport || isDev();
    };

    const isBnSupportUser = () => {
        const authStore = useAuthStore();
        return authStore.isBnSupportUser;
    };

    const hasBnSupportRole = (role) => {
        const authStore = useAuthStore();
        return authStore.hasBnSupportRole(role);
    };

    return {
        devOnly,
        isDev,
        isProaaTenant,
        isRcmRailPreview,
        isMessagingPreview,
        isTenant,
        isProfile,
        isBnSupport,
        isBnSupportUser,
        isAuthorizedDevUser,
        hasBnSupportRole,
    };
};
